import { getShareMeetingSpeakerSummary } from "@/services/share";
import { getMeetingSpeakerSummary } from "@/services/summary";
import { ISpeakerSummary, ISpeakerSummaryApi } from "@/types/summary";
import { create } from "zustand";
import summaryCommonStore from "./summaryCommon.store";
import { AxiosRequestConfig } from "axios";
import { MeetingRecordVisibility, Origin } from "@/constants/share";

interface SummarySpeakerState {
  speakerSummary: ISpeakerSummaryApi | null;
  lastSpeakerSummary: ISpeakerSummaryApi | null;
  newSpeakerSummary: ISpeakerSummary[] | null;
  speakerLoading: boolean;
}

interface SummarySpeakerStore extends SummarySpeakerState {
  fetchSpeakerSummary: (param: { streamName: string; reload: boolean; contentStep: string; cover: boolean }, needThrowError?: boolean, config?: AxiosRequestConfig<unknown>) => Promise<ISpeakerSummaryApi | undefined>;
  modifySpeakerSummary: (newData: ISpeakerSummary[]) => Promise<void>;
  initNewSpeakerSummary: () => void;
  setSpeakerLoading: (loading: boolean) => void;
  setLastSpeakerSummary: (speakerSummary: ISpeakerSummaryApi | null) => void;
  resetLastSpeakerSummary: () => void;
  initStore: () => void;
}

const summarySpeakerState: SummarySpeakerState = {
  speakerSummary: null,
  lastSpeakerSummary: null,
  newSpeakerSummary: null,
  speakerLoading: false,
};

const summarySpeakerStore = create<SummarySpeakerStore>()((set, get) => ({
  ...summarySpeakerState,
  initStore: () => {
    set({ ...summarySpeakerState });
  },
  fetchSpeakerSummary: async (params, needThrowError = false, config) => {
    try {
      let res;
      const { streamName } = params;
      const shareInfo = summaryCommonStore.getState().shareInfo;
      const useComplete = shareInfo?.origin === Origin.MEETING_PLATE && shareInfo?.fileTransEnable;
      const formatParams = {...params, contentStep: useComplete ? "COMPLETE_DOC" : "SRC_DOC"}
      if (shareInfo && summaryCommonStore.getState().shareScope !== MeetingRecordVisibility.orgMember) {
        res = await getShareMeetingSpeakerSummary({ ...formatParams, ...shareInfo }, config);
      } else {
        res = await getMeetingSpeakerSummary(formatParams, config);
      }
      if (!res) {
        throw new Error("获取主讲人概要失败");
      }
      const streamId = summaryCommonStore.getState().streamId;
      if (!streamName.includes(streamId)) {
        return;
      }
      res.srcSpeakers?.forEach((item) => {
        item.roleName = item.roleName || item.role;
      });
      res.completeSpeakers?.forEach((item) => {
        item.roleName = item.roleName || item.role;
      });
      const targetContent = useComplete ? 'completeSpeakers' : 'srcSpeakers';
      let formatData = null;
      if(res[targetContent]){
        formatData = res[targetContent].map(item=>{
          const fromSass = (!item.speakerName || !item.speakerName.length) && item.roleName;
          item.speakerName = fromSass ? [item.roleName] : item.speakerName?.[0] === "未知发言人" ? ['发言人'] : item.speakerName;
          const actualHeadImage = summaryCommonStore.getState().avatarMap.get(item.roleName) || summaryCommonStore.getState().getAvatar(item.roleName);
          item.headImage =  fromSass ? actualHeadImage : item.headImage;
          item.useDefaultIcon = fromSass? true: item.useDefaultIcon;
          return item;
        });
      }
      set({ speakerSummary: {...res, srcSpeakers: formatData} });
      return res;
    } catch (error) {
      console.log('fetchSpeakerSummary error', error);
      // 是否需要抛出异常
      if (needThrowError) {
        throw error;
      }
    }
  },
  modifySpeakerSummary: async (newData) => {
    try {
      set({ newSpeakerSummary: newData });
    } catch (error) {
      console.error(error);
    }
  },
  initNewSpeakerSummary: () => {
    const { modifySpeakerSummary, speakerSummary } = get();
    if (!speakerSummary?.srcSpeakers) {
      return;
    }
    const speakerSummaryContent = speakerSummary.srcSpeakers.length ? speakerSummary.srcSpeakers : speakerSummary.srcSpeakers;
    modifySpeakerSummary(speakerSummaryContent || []);
  },
  setSpeakerLoading: (loading) => {
    set({ speakerLoading: loading });
  },
  setLastSpeakerSummary: (speakerSummary) => {
    set({ lastSpeakerSummary: speakerSummary });
  },
  resetLastSpeakerSummary: () => {
    set({ speakerSummary: get().lastSpeakerSummary, lastSpeakerSummary: null });
  },
}));

export default summarySpeakerStore;
